import { useState } from 'react';
import { Button, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import monthEndAlertsService from 'src/services/apiServices/monthEndAlert/monthEndALerts';

interface RefreshLaborAlertActionProps {
  data: any;
  /** Called after a successful refresh so the parent can refetch the alert list (cleared alerts drop off). */
  onRefreshed: () => void;
}

/**
 * BOPS-126: the per-row "Refresh" action shown on a Treatment-but-No-Labor (MISSING_LABOR_LOG) alert
 * when it is set to Error. Clicking it recomputes that alert's (facility, therapist, day): if labor was
 * logged for that day the alert clears, otherwise it is rebuilt. Only the alert UUID is sent — the
 * backend resolves the rest (see refresh/by-alert/{alertUuid}).
 */
export const RefreshLaborAlertAction: React.FC<RefreshLaborAlertActionProps> = ({
  data,
  onRefreshed,
}) => {
  const [loading, setLoading] = useState(false);

  const handleRefresh = async () => {
    if (!data?.uuid || loading) {
      return;
    }
    setLoading(true);
    try {
      const res = await monthEndAlertsService.refreshTreatmentButNoLaborByAlert(data.uuid);
      const summary = res?.data?.data;
      const deleted = summary?.alertsDeleted ?? 0;
      const generated = summary?.alertsGenerated ?? 0;
      // generated>0 => rebuilt (still no labor that day); deleted>0 & none rebuilt => labor found, cleared;
      // 0/0 => nothing changed (e.g. day is in a closed period, or already resolved) — never call that "cleared".
      if (generated > 0) {
        toast('No labor logged for that day yet — alert kept.');
      } else if (deleted > 0) {
        toast.success('Labor found for that day — alert cleared.');
      } else {
        toast('No change — this alert was not updated.');
      }
      onRefreshed();
    } catch (e) {
      toast.error("Couldn't refresh this alert. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Typography>
      <Button
        onClick={handleRefresh}
        disabled={loading}
      >
        {loading ? 'Refreshing…' : 'Refresh'}
      </Button>
    </Typography>
  );
};
